import { fetchEventSource } from "@microsoft/fetch-event-source";
import { fetchNewAccessToken } from "api/baseQuery";
import { resetAuth, setAuthTokens } from "features/authSlice";
import { AppDispatch } from "store";

interface FetchEventSourceOptions {
  url: string;
  token: string;
  refreshToken: string | null;
  dispatch: AppDispatch;
  onMessage: (data: any) => void;
  onError?: (error: any) => void;
  onDone?: () => void;
}

export class SSEManagerClass {
  private static instance: SSEManagerClass | null = null;
  private abortController: AbortController | null = null;
  public isConnected = false;
  private currentUrl: string | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  static getInstance(): SSEManagerClass {
    if (!this.instance) {
      this.instance = new SSEManagerClass();
    }
    return this.instance;
  }

  connect(options: FetchEventSourceOptions) {
    const { url, token, refreshToken, onMessage, dispatch, onError, onDone } =
      options;

    if (this.isConnected && this.currentUrl === url) {
      console.warn("SSE connection already established.");
      return;
    }
    this.close();
    this.isConnected = true;
    this.currentUrl = url;
    this.abortController = new AbortController();

    const buildUrl = (baseUrl: string) => {
      const lastId = localStorage.getItem("last_notification_id");
      if (!lastId) return baseUrl;
      const separator = baseUrl.includes("?") ? "&" : "?";
      return `${baseUrl}${separator}last_notification_id=${lastId}`;
    };

    const startConnection = async (accessToken: string) => {
      if (this.abortController?.signal.aborted) return;

      try {
        await fetchEventSource(buildUrl(url), {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: this.abortController!.signal,
          onopen: async (response) => {
            const handleTokenRefresh = async () => {
              const currentRefreshToken = refreshToken;
              if (!currentRefreshToken) {
                console.error("No refresh token available. Resetting auth.");
                dispatch(resetAuth());
                this.close();
                return;
              }
              const newTokens = await fetchNewAccessToken(currentRefreshToken);
              if (newTokens?.access_token) {
                const {
                  access_token: newAccessToken,
                  refresh_token: newRefreshToken,
                } = newTokens;
                dispatch(
                  setAuthTokens({
                    accessToken: newAccessToken,
                    refreshToken: newRefreshToken,
                  }),
                );
                console.log("SSE token refreshed successfully");
                startConnection(newAccessToken).catch((_) => {});
              } else {
                console.error("Failed to refresh token. Resetting auth.");
                dispatch(resetAuth());
                this.close();
              }
            };

            if (response.ok) {
              this.isConnected = true;
              console.log(
                `SSE connection opened | last_id=${localStorage.getItem("last_notification_id") ?? "none"}`,
              );
            } else if (response.status === 401 && refreshToken) {
              throw new Error("401");
            }
          },
          onmessage: (event) => {
            try {
              if (!event.event) return;
              const data = JSON.parse(event.data);

              localStorage.setItem("last_notification_id", event.id);
              console.log(
                `SSE message | event=${event.event} | id=${event.id}`,
              );

              onMessage({ data, event: event.event });
            } catch (_) {}
          },
          onerror: (error) => {
            console.error("SSE connection error, will reconnect in 1s");
            this.isConnected = false;
            throw error;
          },
          onclose: () => {
            console.log("SSE connection closed.");
            this.isConnected = false;
            if (onDone) onDone();
          },
        });
      } catch (error: any) {
        if (this.abortController?.signal.aborted) {
          return;
        }

        if (error?.message === "401") {
          const currentRefreshToken = refreshToken;
          if (!currentRefreshToken) {
            dispatch(resetAuth());
            this.close();
            return;
          }
          const newTokens = await fetchNewAccessToken(currentRefreshToken);
          if (newTokens?.access_token) {
            dispatch(
              setAuthTokens({
                accessToken: newTokens.access_token,
                refreshToken: newTokens.refresh_token,
              }),
            );
            startConnection(newTokens.access_token).catch((_) => {});
          } else {
            dispatch(resetAuth());
            this.close();
          }
          return;
        }

        console.error("SSE failed, reconnecting in 1s");
        if (onError) onError(error);
        this.reconnectTimeout = setTimeout(() => {
          startConnection(accessToken).catch((_) => {});
        }, 1000);
      }
    };

    startConnection(token).then();
  }

  close() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.isConnected = false;
    console.log("SSE connection terminated.");
  }
}
